<template>
  <v-container fluid id="settlements-by-payee">
    <v-data-table
    :items="mappedPayees"
    :headers="headers"
    :expanded="expanded"
    item-key="payeeId"
    show-expand
    single-expand
    @click:row="rowClicked"
    style="cursor: pointer;"
    dense
    fixed-header
    height="max(256px, calc(100vh - 485px))"
    :items-per-page="getDataTableDefaultItemsPerPage($vuetify.breakpoint)"
    :footer-props="{
      'items-per-page-options': dataTableItemsPerPage
    }">
      <template #item.amount="{item}">
        {{formatMoney(item.amount)}}
      </template>
      <template #item.grossAmount="{item}">
        {{formatMoney(item.grossAmount)}}
      </template>
      <template #item.netVolume="{item}">
        {{mbfStringFromBf(item.netVolume)}}
      </template>
      <template #item.grossVolume="{item}">
        {{mbfStringFromBf(item.grossVolume)}}
      </template>
      <template #item.rate="{item}">
        <span v-if="payeeRateIsNotApplicable(item)">{{ $t('notApplicable') }}</span>
        <Icon
        v-else-if="item.inlineRates?.length > 1"
        icon="mdi-dots-horizontal"
        iconColor="black"
        :tooltipText="$t('multipleRatesForPayee')"
        @icon-clicked="rateVerificationClicked(item)"
        elementId="slrv"
        />
        <span v-else>
          {{ item.inlineRates[0] ? `${formatMoney(item.inlineRates[0])}/t` : $t('notAvailable') }}
        </span>
      </template>
      <template #item.status="{item}">
        <v-tooltip bottom :color="getSettlementStatus(item.payeeId).color">
          <template v-slot:activator="{ on }">
            <v-chip
            v-on="on"
            small
            style="cursor: pointer;"
            @click.stop="toggleSettlementStatus(item)"
            :class="getSettlementStatus(item.payeeId).color"
            id="settlement-status-chip">
              {{getSettlementStatus(item.payeeId).text}}
            </v-chip>
          </template>
          <span class="subtitle-1 white--text">{{getSettlementStatus(item.payeeId).tooltipText}}</span>
        </v-tooltip>
      </template>
      <template #item.actions="{item}">
        <Icon
        elementId="download-pdf"
        icon="mdi-download"
        iconColor="black"
        :tooltipText="$t('downloadPDF')"
        @icon-clicked.stop="handleDownloadSinglePdf(item)"/>
        <Icon
        v-if="isExportBatch"
        :elementId="idForEmailButton(item)"
        :icon="emailStatusStyleFor(item).icon"
        :iconColor="emailStatusStyleFor(item).color"
        :tooltipColor="emailStatusStyleFor(item).color"
        :tooltipText="emailStatusStyleFor(item).text"
        @icon-clicked.stop="emailSettlementReportDialogOpen(item)"/>
      </template>
      <template #expanded-item="{item}">
        <td :colspan="headers.length + 1" id="sbp-row-expanded">
          <SettlementsByPayeeTable :payee="item" @refresh-settlement="refreshSettlement" :isByproduct="isByproduct"/>
        </td>
      </template>
    </v-data-table>
    <Dialog
    :stateId="dialogId"
    @close="closeDialogs"
    >
      <SBPEmailDialog
      v-if="currentPayee"
      :payeeId="currentPayee.payeeId"
      @submit="handleSendEmail"
      :containerClass="sbpEmailDialogContainerClass"/>
      <RateVerificationDialog
      v-if="rateVerification"
      :payeeId="rateVerificationPayeeId"
      @close="closeRateVerification()"
      @refresh-batch="refreshSettlement()"
      :contractMode="contractMode"/>
    </Dialog>
  </v-container>
</template>

<script>
import { formatMoney, tonStringFromPounds, numberWithCommas, mbfStringFromBf } from '@/utils/NumericMutations'
import { settlementsByPayeeHeaders } from '@/headers/Cycles.js'
import { SettlementColumnContexts } from '@/components/settlements/SettlementKeys.js'
import { generateZippedSettlementReport } from '@/utils/pdf-formatters/ZippedSettlementsReport.js'
import { generateSinglePDFSettlementReport } from '@/utils/pdf-formatters/SinglePDFSettlementsReport.js'
import { mapActions, mapGetters } from 'vuex'
import { PayPeriodStatus, SettlementArchiveStatus, ContractMode, SettlementEmailType, SettlementVerificationType, SettlementVerificationStatus, SettlementInvalidatedReason, UserClaims, PayBy } from '@/utils/Enumerations.js'
import { uploadSettlementReportForEmail } from '@/utils/SettlementReportEmail'
import { uniqueDialogId, dataTableItemsPerPage, getDataTableDefaultItemsPerPage } from '@/utils/componentHelpers'
import { singlePayeeEbSettlementTitle } from '@/utils/pdf-formatters/GenericSettlementsFunctions'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import saveAs from 'file-saver'
import { kebabCase } from 'lodash'

const TONS_DECIMALS = 3

export default {
  name: 'SettlementsByPayee',

  components: {
    SettlementsByPayeeTable: () => import('@/components/settlements/accounts-payable-module/settlements-by-payee/SettlementsByPayeeTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    SBPEmailDialog: () => import('@/components/settlements/accounts-payable-module/settlements-by-payee/SBPEmailDialog.vue'),
    RateVerificationDialog: () => import('@/components/settlements/rate-verification/RateVerificationDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    settlementsObject: {
      type: Object,
      required: true
    },
    isByproduct: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dataTableItemsPerPage,
    expanded: [],
    rateVerification: false,
    rateVerificationPayeeId: undefined,
    currentPayee: null,
    dialogId: uniqueDialogId('settlements-by-payee'),
    verificationMap: {},
    payees: [],
    emailRecords: [],
    contactGroupsByAccountId: {}
  }),

  computed: {
    ...mapGetters('settlements', ['dateConfiguration', 'settlementPayeeStatuses', 'settlementContractStatuses', 'currentSubModuleKey']),
    mappedPayees () {
      if (this.settlementsObject === undefined) { return [] }
      const payeesModified = this.settlementsObject.payees
      payeesModified.forEach(e => {
        Object.assign(e, {
          loads: this.getNumberOfLoads(e),
          netTons: this.getPayeeNetTonsTotal(e),
          grossTons: this.getPayeeGrossTonsTotal(e),
          amount: this.getPayeeTotalUnformatted(e),
          grossAmount: this.getPayeeTotalUnformatted(e, true),
          status: this.getSettlementStatus(e.payeeId).text,
          inlineRates: e.inlineRates
        })
      })
      return payeesModified
    },

    hasVolumeData () {
      return this.mappedPayees.some(p => p.netVolume > 0 || p.grossVolume > 0)
    },

    headers () {
      const settlementContext = this.isByproduct ? SettlementColumnContexts.ByproductSettlementsByPayee : SettlementColumnContexts.SettlementsByPayee
      const columns = settlementsByPayeeHeaders(!this.isByproduct && this.hasVolumeData, settlementContext).getColumns()
      columns.forEach(column => column.setCellClass('sbp-table-cell'))
      return columns
    },

    isExportBatch () {
      return this.dateConfiguration?.exportBatch.exportBatchId > 0
    },

    exportBatchId () {
      return this.dateConfiguration.exportBatch.exportBatchId
    },

    exportBatchExportedUnixMs () {
      return (this.dateConfiguration.exportBatch)
        ? Date.parse(`${this.dateConfiguration.exportBatch.exportDate}Z`)
        : undefined
    },

    payPeriodId () {
      return this.dateConfiguration.payPeriod.payPeriodId
    },

    payPeriodIsOpen () {
      return this.dateConfiguration.payPeriod.status === PayPeriodStatus.Open.value
    },

    contractMode () {
      return this.isByproduct ? ContractMode.Byproducts : ContractMode.Logs
    },

    sbpEmailDialogContainerClass () {
      return kebabCase(this.contractMode.label)
    },

    verificationType () {
      return this.isByproduct
        ? SettlementVerificationType.Byproducts.value
        : SettlementVerificationType.Logs.value
    },

    settlementEmailedAccountIds () {
      return new Set(this.emailRecords
        .filter(record => record.emailType === SettlementEmailType.SettlementsByPayee.value)
        .map(record => record.accountId))
    }
  },

  watch: {
    settlementsObject: {
      handler (val) {
        this.payees = val?.payees ?? []
      },
      deep: true,
      immediate: true
    }
  },

  created () {
    this.refreshSettlementStatuses()
    this.refreshEmailDeliveryData()
  },

  methods: {
    ...mapActions('settlements', [
      'clearStaleSubModuleData',
      'setCurrentModule',
      'refreshModuleData',
      'createSettlementEmailRequest',
      'downloadSubmoduleBlob',
      'createSettlementVerification',
      'updateSettlementVerification',
      'fetchSettlementVerifications',
      'deleteSettlementVerification',
      'fetchSettlementEmailRecords'
    ]),
    ...mapActions('account-contacts', ['fetchContactsForAccounts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('export-batch', ['fetchExportBatches']),
    formatMoney,
    tonStringFromPounds,
    mbfStringFromBf,
    getDataTableDefaultItemsPerPage,

    rowClicked (item) {
      this.expanded = []
      this.$nextTick(() => {
        this.expanded = [item]
      })
    },

    getNumberOfLoads (payee) {
      let allContractLoads = []
      payee.contracts.forEach(contract => {
        allContractLoads = [...allContractLoads, ...contract.loadSummaries]
      })
      return [...new Set(allContractLoads.map(load => load.ticketNumber))].length
    },

    getUnixTimestamp90DaysAgo () {
      return Date.now() - (1000 * 60 * 60 * 24 * 90)
    },

    getPayeeTotalUnformatted (payee, isGross) {
      const ticketTotal = payee.contracts.reduce((total, c) => total + (isGross ? c.grossAmount : c.amount), 0)
      const tractPaymentTotal = payee.tractPayables?.map(payable => payable.amount).reduce((prev, curr) => prev + curr, 0) ?? 0
      const advanceTotal = payee.advances.map(advance => advance.amount).reduce((prev, curr) => prev + curr, 0)
      const correctionTotal = payee.corrections.reduce((prev, curr) => prev + curr.amount, 0)
      const accountPaymentTotal = payee.accountPayables?.reduce((total, payable) => total + payable.amount, 0)
      return tractPaymentTotal + advanceTotal + ticketTotal + correctionTotal + accountPaymentTotal
    },

    getPayeeGrossTonsTotal ({ contracts }) {
      const value = contracts.reduce((total, c) => total + c.grossTons, 0)
      return numberWithCommas(value, TONS_DECIMALS)
    },

    getPayeeNetTonsTotal ({ contracts }) {
      const value = contracts.reduce((total, c) => total + c.netTons, 0)
      return numberWithCommas(value, TONS_DECIMALS)
    },

    getAverageRate ({ contracts }, isNet) {
      const amount = contracts.reduce((total, c) => total + c.grossAmount, 0)
      const weight = isNet
        ? contracts.reduce((total, c) => total + c.netTons, 0)
        : contracts.reduce((total, c) => total + c.grossTons, 0)

      const o = Number((amount / weight).toFixed(2))

      return (!Number.isNaN(o) && Number.isFinite(o))
        ? formatMoney(o)
        : this.$t('notAvailable')
    },

    async toggleSettlementStatus (payee) {
      if (this.dateConfiguration?.payPeriod.status === PayPeriodStatus.Exported.value || this.isExportBatch) return

      const verification = this.verificationMap[payee.payeeId]

      if (!userAssignedClaim(UserClaims.ContractManager)) {
        this.setSnackError(this.$t('userDoesNotHavePermissionToVerifySettlements'))
        return
      }

      if (verification) {
        if (verification.status === SettlementVerificationStatus.Verified.value) {
          await this.deleteSettlementVerification(verification.settlementVerificationId)
        } else {
          await this.updateSettlementVerification({
            settlementVerificationId: verification.settlementVerificationId,
            status: SettlementVerificationStatus.Verified.value,
            payPeriodId: this.payPeriodId,
            accountId: payee.payeeId,
            modifiedAt: verification.modifiedAt,
            type: this.verificationType
          })
        }
      } else {
        await this.createSettlementVerification({
          payPeriodId: this.payPeriodId,
          accountId: payee.payeeId,
          type: this.verificationType
        })
      }

      this.refreshSettlementStatuses()
    },

    getSettlementStatus (payeeId) {
      if (this.dateConfiguration.payPeriod.status === PayPeriodStatus.Exported.value || this.isExportBatch) {
        return {
          text: this.$t('verified'),
          tooltipText: this.$t('verified'),
          color: 'success white--text'
        }
      }

      const verification = this.verificationMap[payeeId]

      switch (verification?.status) {
        case SettlementVerificationStatus.Verified.value:
          const date = verification.modifiedAt
            ? utcToLocalDate(verification.modifiedAt, 'L - LT')
            : utcToLocalDate(verification.createdAt, 'L - LT')
          return {
            text: this.$t('verified'),
            tooltipText: this.$t('verifiedByUserAt', { user: verification.verifiedBy, date }),
            color: 'success white--text'
          }
        case SettlementVerificationStatus.Invalidated.value:
          return {
            text: this.$t('invalidated'),
            tooltipText: this.$t(SettlementInvalidatedReason.forInt(verification.settlementInvalidatedReason).label, { date: utcToLocalDate(verification.modifiedAt, 'L - LT') }),
            color: 'warning white--text'
          }
        default:
          return {
            tooltipText: this.$t('clickToVerify'),
            text: this.$t('needsReview'),
            color: 'blue-grey lighten-2 white--text'
          }
      }
    },

    async downloadSettlementReportZip (payee) {
      const payeeSettlement = { ...this.settlementsObject }
      payeeSettlement.payees = this.settlementsObject.payees.filter(p => p.payeeId === payee.payeeId)
      return await generateZippedSettlementReport(payeeSettlement, { exportBatch: this.dateConfiguration?.exportBatch })
    },

    async downloadSettlementReportPdf (payee) {
      return await generateSinglePDFSettlementReport({
        ...this.settlementsObject,
        payees: [payee]
      }, {
        filenamePrefix: payee?.payeeName,
        exportBatch: this.dateConfiguration?.exportBatch
      })
    },

    async emailSettlementReport (payee, emailAddress, documentBlob) {
      try {
        await uploadSettlementReportForEmail(emailAddress, documentBlob)
        this.setSnack(this.$t('sentSettlementReportToEmailAddress', { emailAddress }))
      } catch (e) {
        console.error(e)
        this.setSnackError(this.$t('failedToSendEmailToEmailAddress', { emailAddress }))
      }
    },

    emailSettlementReportDialogOpen (payee) {
      if (!this.isExportBatch) {
        this.setSnackError(this.$t('cannotEmailUnexportedSettlements'))
        return
      }
      const eb = this.dateConfiguration.exportBatch
      if (eb?.settlementArchiveStatus !== SettlementArchiveStatus.Success.value) {
        this.setSnack(this.$t('documentGenerationInProgress'))
        return
      }
      this.currentPayee = payee
      this.openOrUpdateDialog({ id: this.dialogId, width: '600px' })
    },

    emailSettlementReportDialogClose () {
      this.currentPayee = null
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async handleDownloadSinglePdf (payee) {
      const eb = this.dateConfiguration.exportBatch
      if (eb?.exportBatchId) {
        if (eb.settlementArchiveStatus !== SettlementArchiveStatus.Success.value) {
          await this.fetchExportBatches(eb.payPeriodId)
        }
        const filename = singlePayeeEbSettlementTitle({ payee: payee.payeeName, exportDate: utcToLocalDate(eb.exportDate), label: eb.label })
        await this.downloadSubmoduleBlob({ accountId: payee.payeeId, filename })
      } else {
        const { blob, filename } = await this.downloadSettlementReportPdf(payee)
        saveAs(blob, filename)
      }
    },

    async handleSendEmail (request) {
      if (request.type === 'upload') {
        await this.createSettlementEmailRequest({ ...request })
        this.closeDialogsAtOrAbove(this.dialogId)
        await this.refreshEmailDeliveryData()
        return
      }

      const { blob, filename } = await this.downloadSettlementReportPdf(this.currentPayee)
      saveAs(blob, filename)
      window.open(`mailto:${request.emailAddress}?subject=Settlement Sheet`, '_blank')

      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async emailDownloadConfirm () {
      const pdfBlob = await this.downloadSettlementReportPdf(this.currentPayee)
      this.emailSettlementReport(this.currentPayee, pdfBlob)
      this.emailSettlementReportDialogClose()
    },

    emailStatusStyleFor (item) {
      if (this.settlementEmailedAccountIds.has(item.payeeId)) {
        return {
          icon: 'mdi-email-check-outline',
          color: 'success',
          text: this.$t('emailSent')
        }
      }

      return this.recipientCountFor(item) > 0
        ? {
          icon: 'mdi-email-outline',
          color: 'black',
          text: this.$t('emailSettlement')
        } : {
          icon: 'mdi-email-plus-outline',
          color: 'error',
          text: this.$t('addPayeeContact')
        }
    },

    recipientCountFor (item) {
      return this.contactGroupsByAccountId[item.payeeId]?.contacts.length ?? 0
    },

    rateVerificationClicked (payee) {
      this.rateVerification = true
      this.rateVerificationPayeeId = payee.payeeId
      this.openOrUpdateDialog({ id: this.dialogId, width: '70%' })
    },

    closeRateVerification () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.rateVerification = false
      this.rateVerificationPayeeId = undefined
    },

    closeDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.rateVerification = false
      this.rateVerificationPayeeId = undefined
      this.currentPayee = null
    },

    async refreshSettlement () {
      this.clearStaleSubModuleData({ isByproduct: this.isByproduct })
      await this.refreshModuleData(false)
    },

    payeeRateIsNotApplicable (payee) {
      return payee.contracts.some(contract =>
        contract.paymentRollup.some(activity =>
          activity.payBy === PayBy.Volume.value || activity.isDeduction
        )
      )
    },

    idForEmailButton (item) {
      if (this.settlementEmailedAccountIds.has(item.payeeId)) return 'email-sent'
      if (this.recipientCountFor(item) > 0) return 'email-settlement'
      return 'add-recipient'
    },

    async refreshSettlementStatuses () {
      const verifications = await this.fetchSettlementVerifications(this.verificationType)

      this.verificationMap = verifications.reduce((acc, v) => {
        acc[v.accountId] = v
        return acc
      }, {})
    },

    async refreshEmailDeliveryData () {
      if (!this.isExportBatch) return

      const [emailRecords, contactGroups] = await Promise.all([
        this.fetchSettlementEmailRecords(this.isByproduct),
        this.fetchContactsForAccounts({
          accountIds: this.payees.map(payee => payee.payeeId),
          limitToSettlementRecipients: true
        })
      ])

      this.emailRecords = emailRecords
      this.contactGroupsByAccountId = Object.fromEntries(contactGroups.map(group => [group.accountId, group]))
    }
  }
}
</script>
